/**
 * Página de alta y edición de una lista de inventario
 */
import React, { useContext, useEffect, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Box, Alert, Button, Stack } from "@mui/material";

import { StateContext } from "../../../components/context/StateProvider";
import Toasts from "../../../components/notificacion/Toasts";
import InventariosService from "../api/inventarios.api";
import { FormularioInventario } from "../components/forms";
import { CargandoInventario } from "../components/ui";
import { INVENTARIO_ESTADO } from "../constants";
import { BOTON_SECUNDARIO_SX } from "../../../components/ui/buttonStyles";

// DECISION (TASK-010): página delgada, mismo reparto que RangoFormPage. Toda
// la interacción de campos vive en FormularioInventario (controlado, TASK-009);
// esta página solo resuelve el dato inicial en edición y llama directamente a
// InventariosService.crear/actualizar. NO usa useInventarios (TASK-006): ese
// hook orquesta el LISTADO admin (carga inicial + recarga tras cada
// mutación), y aquí no hay ningún listado que mostrar — instanciarlo solo
// para sus funciones crear/actualizar disparaba un listar() inútil en cada
// alta/edición (limpieza post-review, ver notes de TASK-019).
// Rutas que consume (routes.js): /inventarios/nuevo y /inventarios/:id/editar
const InventarioFormPage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { state, updateState, refrescarContadorInventarios } = useContext(StateContext);
    const esEdicion = Boolean(id);

    const [inventario, setInventario] = useState(null);
    // Patrón del proyecto (ver useRangoFormData): solo se bloquea la UI al
    // EDITAR. Crear no necesita cargar nada, el formulario aparece de inmediato.
    const [cargando, setCargando] = useState(esEdicion);
    const [error, setError] = useState(null);
    const [guardando, setGuardando] = useState(false);
    useEffect(() => {
        if (!esEdicion) return;

        let vivo = true;
        setCargando(true);
        setError(null);

        InventariosService.getById(id)
            .then((data) => { if (vivo) setInventario(data); })
            .catch((err) => { if (vivo) setError(err.message || "No se pudo cargar el inventario solicitado."); })
            .finally(() => { if (vivo) setCargando(false); });

        return () => { vivo = false; };
    }, [id, esEdicion]);

    const volverAlListado = useCallback(() => navigate("/inventarios"), [navigate]);

    const handleGuardar = useCallback(async (datos) => {
        setGuardando(true);
        try {
            if (esEdicion) {
                await InventariosService.actualizar(id, datos);
            } else {
                await InventariosService.crear(datos);
                refrescarContadorInventarios?.();
            }
            const mensaje = esEdicion ? "Inventario actualizado correctamente" : "Inventario creado correctamente";
            updateState({ toasts: [...state.toasts, Toasts.generateToast(mensaje, "success")] });
            volverAlListado();
        } catch (err) {
            updateState({
                toasts: [...state.toasts, Toasts.generateToast(err.message || "No se pudo guardar el inventario.", "error")],
            });
        } finally {
            setGuardando(false);
        }
    }, [esEdicion, id, refrescarContadorInventarios, state.toasts, updateState, volverAlListado]);

    if (cargando) return <CargandoInventario titulo="Editar lista de inventario" />;

    if (error) {
        return (
            <Stack spacing={2} sx={{ width: "100%" }}>
                <Alert severity="error">{error}</Alert>
                <Button variant="outlined" onClick={volverAlListado} sx={{ ...BOTON_SECUNDARIO_SX, alignSelf: "flex-start" }}>
                    Volver al listado
                </Button>
            </Stack>
        );
    }

    if (esEdicion && inventario && inventario.estado !== INVENTARIO_ESTADO.PENDIENTE) {
        return (
            <Stack spacing={2} sx={{ width: "100%" }}>
                <Alert severity="warning">Este inventario ya está en curso y no se puede editar</Alert>
                <Button variant="outlined" onClick={volverAlListado} sx={{ ...BOTON_SECUNDARIO_SX, alignSelf: "flex-start" }}>
                    Volver al listado
                </Button>
            </Stack>
        );
    }

    return (
        <Box sx={{ width: "100%" }}>
            <FormularioInventario
                inventario={inventario}
                guardando={guardando}
                onGuardar={handleGuardar}
                onCancel={volverAlListado}
            />
        </Box>
    );
};

export default InventarioFormPage;
